'use client';

import type { ReactNode } from 'react';
import { VStack } from '@terra/ui-styled-system';
import { MetaDefault } from '../../../../../components/typography';
import { useTranslations } from 'next-intl';
import type { CardRenderContext } from '../../tool-config-types';
import { FlatToolCard } from '../shared/flat-tool-card';
import { getHITLArtifactHeaderActions } from '../shared/hitl-artifact-header-actions';
import {
  HitlLabeledValue,
  HitlLabeledValues,
} from '../shared/hitl-labeled-values';
import { toRecord } from '../shared/network-tool-card-utils';

type ScannerCardArgs = {
  targetDomains?: string[];
  subdomains?: string[];
  ips?: string[];
  targetRanges?: string[];
  timeoutMs?: number;
};

function readStringArray(
  record: Record<string, unknown>,
  key: string,
): string[] | undefined {
  const value = record[key];

  if (!Array.isArray(value)) {
    return undefined;
  }

  const items = value.filter(
    (entry): entry is string =>
      typeof entry === 'string' && entry.trim().length > 0,
  );

  return items.length > 0 ? items : undefined;
}

function parseScannerArgs(input: unknown): ScannerCardArgs {
  const record = toRecord(input);

  if (!record) {
    return {};
  }

  const config = toRecord(record.config);
  const timeoutMs =
    typeof config?.timeoutMs === 'number' && config.timeoutMs > 0
      ? config.timeoutMs
      : undefined;

  return {
    targetDomains:
      readStringArray(record, 'targetDomains') ??
      readStringArray(record, 'seedDomains'),
    subdomains: readStringArray(record, 'subdomains'),
    ips: readStringArray(record, 'ips'),
    targetRanges:
      readStringArray(record, 'targetRanges') ??
      readStringArray(record, 'seedIpRanges'),
    timeoutMs,
  };
}

function formatTimeoutMs(timeoutMs: number): string {
  if (timeoutMs >= 60000) {
    return `${Math.round(timeoutMs / 60000)}m`;
  }

  return `${Math.round(timeoutMs / 1000)}s`;
}

function ScannerCardBody({
  args,
  error,
}: {
  args: ScannerCardArgs;
  error?: string;
}) {
  const t = useTranslations('designSystem.chat.externalNetworkScannerTool');

  return (
    <VStack alignItems="stretch" gap={2} width="100%">
      {args.targetDomains ? (
        <HitlLabeledValues
          label={t('targetDomainsLabel')}
          values={args.targetDomains}
        />
      ) : null}
      {args.subdomains ? (
        <HitlLabeledValues
          label={t('subdomainsLabel')}
          values={args.subdomains}
        />
      ) : null}
      {args.ips ? (
        <HitlLabeledValues label={t('ipsLabel')} values={args.ips} />
      ) : null}
      {args.targetRanges ? (
        <HitlLabeledValues
          label={t('targetRangesLabel')}
          values={args.targetRanges}
        />
      ) : null}
      {args.timeoutMs ? (
        <HitlLabeledValue
          label={t('timeoutLabel')}
          value={formatTimeoutMs(args.timeoutMs)}
        />
      ) : null}
      {error ? <MetaDefault color="fg.error">{error}</MetaDefault> : null}
    </VStack>
  );
}

function hasScannerBody(args: ScannerCardArgs, error?: string): boolean {
  return Boolean(
    args.targetDomains ||
      args.subdomains ||
      args.ips ||
      args.targetRanges ||
      args.timeoutMs ||
      error,
  );
}

export function renderExternalNetworkScannerCard(
  ctx: CardRenderContext,
): ReactNode {
  const { toolCallData, toolName } = ctx;
  const isStarted =
    toolCallData.status === 'started' || toolCallData.status === 'in-progress';

  if (
    !isStarted &&
    toolCallData.status !== 'completed' &&
    toolCallData.status !== 'error'
  ) {
    return undefined;
  }

  const args = parseScannerArgs(toolCallData.input);
  const error =
    toolCallData.status === 'error' ? toolCallData.error : undefined;

  return (
    <FlatToolCard
      title={toolName}
      isRunning={toolCallData.status === 'in-progress'}
      headerActions={getHITLArtifactHeaderActions(ctx)}
    >
      {hasScannerBody(args, error) ? (
        <ScannerCardBody args={args} error={error} />
      ) : undefined}
    </FlatToolCard>
  );
}
